import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Eye, EyeOff, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { AuthShell } from "@/pages/Login";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Password reset. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      side={
        <>
          <p className="font-editorial italic text-[color:var(--ink-muted)]">Access · Recovery</p>
          <h1 className="mt-6 font-editorial text-5xl font-normal leading-[1.05] tracking-tight text-[color:var(--ink)] sm:text-6xl">
            Choose a new password.
          </h1>
          <p className="mt-6 max-w-md text-[16px] leading-relaxed text-[color:var(--ink-body)]">
            Pick something strong you don't use elsewhere. You'll use it the next time you sign in.
          </p>
        </>
      }
    >
      {!token ? (
        <div className="grid gap-5 text-center" data-testid="reset-no-token">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-orange-600">
            <ShieldAlert size={26} />
          </div>
          <h2 className="font-editorial text-2xl text-[color:var(--ink)]">Invalid reset link</h2>
          <p className="text-[15px] text-[color:var(--ink-body)]">This link is missing its token. Request a new reset link.</p>
          <Link to="/forgot-password" className="btn-edge mt-2 w-full justify-center" data-testid="reset-request-new">
            Request a new link <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="grid gap-5" data-testid="reset-form">
          <div>
            <label className="mb-2 block font-editorial italic text-[14px] text-[color:var(--ink-muted)]">New password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="reset-password"
                className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 pr-12 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
              />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label="Toggle password" data-testid="reset-toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-2 block font-editorial italic text-[14px] text-[color:var(--ink-muted)]">Confirm password</label>
            <input
              type={showPw ? "text" : "password"}
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              data-testid="reset-confirm"
              className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
            />
          </div>
          <button type="submit" disabled={submitting} data-testid="reset-submit" className="btn-edge mt-2 w-full justify-center disabled:cursor-wait disabled:opacity-70">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Reset password <ArrowRight size={16} /></>}
          </button>
          <p className="mt-2 text-center text-[14px] text-[color:var(--ink-muted)]">
            <Link to="/login" className="font-medium text-[color:var(--evergreen)] hover:underline" data-testid="reset-link-login">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
